import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import api from '../api';
import { useGet, useGetById, useGetOne, usePost, usePut, useRemove, useSoftDelete } from '../../apiHooks';
import { Currency } from '../types';
import { formatData } from '../../providersHelpers';
import { ByIdStructure } from '../../../types';
import { getDataOptions } from '../../../utils/arrayUtils';
import { Context as AuthContext } from '../../auth/context';

import Context from './context';

interface Props {
    children: React.ReactElement;
}

const CurrenciesProvider = ({ children }: Props) => {
    const [data, setData] = useState<ByIdStructure<Currency>>({ ids: [], byId: {} });
    const [selected, setSelected] = useState<Currency | null>(null);
    const [, setError] = useState<Error>();
    const fetchPromiseRef = useRef<Promise<any> | null>(null);
    const dataLoadedRef = useRef(false);

    const authContext = useContext(AuthContext);

    const handleGetData = useCallback((res) => {
        setData(formatData(res));
        dataLoadedRef.current = true;
        fetchPromiseRef.current = null;
        return res;
    }, []);
    const baseGet = useGet(api, data, handleGetData, setError);

    // Wrapper to prevent duplicate fetches - returns cached data or in-flight promise
    const get = useCallback(
        (params?: any, options?: any, updateState?: boolean) => {
            // If data is already loaded, return it
            if (dataLoadedRef.current) {
                return Promise.resolve(data);
            }
            // If a fetch is in progress, return the existing promise
            if (fetchPromiseRef.current) {
                return fetchPromiseRef.current;
            }
            // Otherwise, start a new fetch
            fetchPromiseRef.current = baseGet(params, options, updateState);
            return fetchPromiseRef.current;
        },
        [baseGet, data]
    );

    useEffect(() => {
        if (authContext.currUser) {
            get({});
        }
    }, [authContext.currUser]); // eslint-disable-line

    const getById = useGetById(api, selected, setSelected, setError);

    const getOne = useGetOne(api, setSelected, setError);

    const handleSetData = useCallback((item, newData) => {
        setData(newData);
    }, []);
    const post = usePost(api, data, handleSetData, setError);

    const handlePutCallback = useCallback((response, newData) => {
        setData(newData);
        setSelected(response);
    }, []);
    const put = usePut(api, data, handlePutCallback, setError);

    const handleDeleteCallback = useCallback((id, newData) => {
        setData(newData);
        setSelected(null);
        return id;
    }, []);
    const remove = useRemove(api, data, handleDeleteCallback, setError);

    const softDelete = useSoftDelete(api, data, handleDeleteCallback, setError);

    const dataAsArray = useMemo(() => getDataOptions(data), [data]);

    const getSymbolByCurrency = useCallback(
        (currency) => {
            if (!currency) {
                return '';
            }
            const currencyId = typeof currency !== 'object' ? currency : currency?._id;
            return data.byId?.[currencyId]?.symbol || '';
        },
        [data.byId]
    );

    const getCodeByCurrency = useCallback(
        (currency) => {
            if (!currency) {
                return '';
            }
            const currencyId = typeof currency !== 'object' ? currency : currency?._id;
            return data.byId?.[currencyId]?.code || '';
        },
        [data.byId]
    );

    const contextValue = useMemo(
        () => ({
            get,
            getById,
            getOne,
            post,
            put,
            data,
            dataAsArray,
            setData,
            selected,
            setSelected,
            remove,
            softDelete,
            getSymbolByCurrency,
            getCodeByCurrency
        }),
        [
            get,
            getById,
            getOne,
            post,
            put,
            data,
            dataAsArray,
            setData,
            selected,
            setSelected,
            remove,
            softDelete,
            getSymbolByCurrency,
            getCodeByCurrency
        ]
    );

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default CurrenciesProvider;
